// People with several positions. One employee code = one person (fixed name and emails) who can hold
// several roles, teams and Namecodes. Pure helpers shared by the UI and the server.
import type { Position, Role, User } from "./types";

/** Which position counts as the main one (landing page, labels): most privileged first. */
export const ROLE_ORDER: Role[] = ["LEADER_AI", "GN", "EDITOR", "STAFF_AI", "OUTPUT_QC"];

type PersonLike = Partial<Pick<User, "role" | "teamId" | "namecode" | "email" | "emails" | "positions">> | null | undefined;

/** All positions; older records without a positions list count as one position. */
export function positionsOf(u: PersonLike): Position[] {
  if (!u) return [];
  if (u.positions?.length) return u.positions;
  return u.role ? [{ key: u.email ?? "", role: u.role, teamId: u.teamId ?? null, namecode: u.namecode ?? null, email: u.email ?? "", source: "form", createdAt: "" }] : [];
}

const unique = <T,>(xs: T[]) => [...new Set(xs)];

export const rolesOf = (u: PersonLike): Role[] => unique(positionsOf(u).map((p) => p.role)).sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b));
export const hasRole = (u: PersonLike, role: Role) => positionsOf(u).some((p) => p.role === role);
export const teamsOf = (u: PersonLike): string[] => {
  const fromPositions = unique(positionsOf(u).map((p) => p.teamId).filter((t): t is string => !!t));
  return fromPositions.length ? fromPositions : u?.teamId ? [u.teamId] : [];
};
/** Teams in which the person works as an editor (Namecodes belong to these). */
export const editorTeamsOf = (u: PersonLike): string[] => unique(positionsOf(u).filter((p) => p.role === "EDITOR").map((p) => p.teamId).filter((t): t is string => !!t));
export const namecodesOf = (u: PersonLike): string[] => unique(positionsOf(u).filter((p) => p.role === "EDITOR").map((p) => p.namecode).filter((n): n is string => !!n));
export const emailsOf = (u: PersonLike): string[] => (u?.emails?.length ? u.emails : u?.email ? [u.email] : []);

export function mainPosition(positions: Position[]): Position | null {
  return [...positions].sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.createdAt.localeCompare(b.createdAt))[0] ?? null;
}

/** One sign-up row (or a position a Leader added), before rows are grouped into people. */
export interface Account {
  key: string;
  email: string;
  employeeCode: string;
  fullName: string;
  role: Role;
  teamId: string | null;
  namecode: string | null;
  createdAt: string;
  source: Position["source"];
  status: "ACTIVE" | "DISABLED";
  statusReason: string | null;
  /** Waiting for a Leader: kept so the person exists (and sees "waiting"), but grants nothing yet. */
  pending?: boolean;
  /** Position removed by a Leader: grants nothing, but its email still signs the person in. */
  removed?: boolean;
}

/**
 * Groups accounts by employee code. Name, code and the first email come from the first registration;
 * every approved account adds a position (duplicates of role + team + Namecode collapse).
 */
export async function mergePeople(accounts: Account[], idFor: (email: string) => Promise<string>): Promise<Omit<User, "avatarUrl">[]> {
  const groups = new Map<string, Account[]>();
  for (const a of [...accounts].sort((x, y) => x.createdAt.localeCompare(y.createdAt))) {
    const list = groups.get(a.employeeCode) ?? [];
    list.push(a);
    groups.set(a.employeeCode, list);
  }
  const people: Omit<User, "avatarUrl">[] = [];
  for (const [code, list] of groups) {
    const withEmail = list.filter((a) => a.email);
    const first = withEmail[0] ?? list[0];
    const positions: Position[] = [];
    for (const a of list.filter((x) => !x.pending && !x.removed)) {
      if (positions.some((p) => p.role === a.role && p.teamId === a.teamId && p.namecode === a.namecode)) continue;
      positions.push({ key: a.key, role: a.role, teamId: a.teamId, namecode: a.namecode, email: a.email, source: a.source, createdAt: a.createdAt });
    }
    const main = mainPosition(positions);
    const disabled = list.find((a) => a.status === "DISABLED");
    const onlyPending = !main && list.some((a) => a.pending);
    people.push({
      id: await idFor(first.email || `${code}@people.local`),
      email: first.email,
      emails: unique(withEmail.map((a) => a.email)),
      employeeCode: code,
      fullName: first.fullName,
      status: disabled ? "DISABLED" : onlyPending ? "PENDING" : "ACTIVE",
      role: main?.role ?? null,
      teamId: main?.teamId ?? first.teamId,
      namecode: main?.role === "EDITOR" ? main.namecode : (positions.find((p) => p.role === "EDITOR")?.namecode ?? null),
      positions,
      permissionsOverride: null,
      createdAt: first.createdAt,
      statusReason: disabled?.statusReason ?? (onlyPending ? "Waiting for Leader approval" : null),
    });
  }
  return people;
}
